// Pure multipart editing operations. Parts share the form's conductor grid,
// so a newly-created part always starts with one empty PartMeasure per
// FormMeasure. Existing scores and their nested content are never mutated.

import type {
  Clef, Part, PartInstrument, PartRole, Score, StaffDef, Transposition, VoiceItem,
} from './types';

export type PartPreset = 'lead' | 'guitar' | 'bass' | 'piano';

interface PresetDefinition {
  name: string;
  shortName: string;
  instrument: PartInstrument;
  role: PartRole;
  clefs: Clef[];
  transposition?: Transposition;
}

const PRESETS: Record<PartPreset, PresetDefinition> = {
  lead: {
    name: 'Lead',
    shortName: 'Lead',
    instrument: 'lead',
    role: 'lead',
    clefs: ['treble'],
  },
  guitar: {
    name: 'Guitar',
    shortName: 'Gtr.',
    instrument: 'guitar',
    role: 'comp',
    clefs: ['treble8vb'],
  },
  bass: {
    name: 'Bass',
    shortName: 'Bass',
    instrument: 'bass',
    role: 'bass',
    clefs: ['bass'],
    // Bass reads one octave above its sounding (stored concert) pitch.
    transposition: { degrees: 7, semitones: 12 },
  },
  piano: {
    name: 'Piano',
    shortName: 'Pno.',
    instrument: 'piano',
    role: 'comp',
    clefs: ['treble', 'bass'],
  },
};

function firstUnusedId(base: string, used: ReadonlySet<string>): string {
  if (!used.has(base)) return base;
  let suffix = 2;
  while (used.has(`${base}:${suffix}`)) suffix += 1;
  return `${base}:${suffix}`;
}

function firstUnusedName(base: string, parts: readonly Part[]): string {
  const used = new Set(parts.map((part) => part.name));
  if (!used.has(base)) return base;
  let suffix = 2;
  while (used.has(`${base} ${suffix}`)) suffix += 1;
  return `${base} ${suffix}`;
}

/** Build an empty preset part aligned exactly to the score's form. */
export function createPart(score: Score, preset: PartPreset): Part {
  const definition = PRESETS[preset];
  const partId = firstUnusedId(`part:${preset}`, new Set(score.parts.map((part) => part.id)));
  const idStem = partId.slice('part:'.length);
  const staves: StaffDef[] = definition.clefs.map((clef, index) => ({
    id: `staff:${idStem}:${index}`,
    clef,
    staffType: 'standard',
  }));

  return {
    id: partId,
    name: firstUnusedName(definition.name, score.parts),
    shortName: definition.shortName,
    instrument: definition.instrument,
    role: definition.role,
    ...(definition.transposition ? { transposition: { ...definition.transposition } } : {}),
    staves,
    measures: score.form.map((measure) => ({
      id: `pm:${idStem}:${measure.id}`,
      voices: [],
    })),
  };
}

/** Append an aligned preset part, preserving all existing part objects. */
export function addPart(score: Score, preset: PartPreset): Score {
  return { ...score, parts: [...score.parts, createPart(score, preset)] };
}

/** Trim and apply a display name. Blank, unchanged and unknown edits are no-ops. */
export function renamePart(score: Score, partId: string, name: string): Score {
  const nextName = name.trim();
  if (!nextName) return score;
  const index = score.parts.findIndex((part) => part.id === partId);
  if (index < 0 || score.parts[index].name === nextName) return score;

  const parts = [...score.parts];
  parts[index] = { ...parts[index], name: nextName };
  return { ...score, parts };
}

function collectEventIds(items: readonly VoiceItem[], out: Set<string>): void {
  for (const item of items) {
    if (item.kind === 'tuplet') collectEventIds(item.items, out);
    else out.add(item.id);
  }
}

/**
 * Remove a part while keeping Score Studio structurally valid. Removing the
 * final notation part deliberately leaves a chord-only call sheet; writing a
 * note later recreates the historical Lead part on demand. Unknown ids are
 * safe no-ops. Spanners touching removed content leave with the part.
 */
export function removePart(score: Score, partId: string): Score {
  const index = score.parts.findIndex((part) => part.id === partId);
  if (index < 0) return score;

  const removedEventIds = new Set<string>();
  for (const measure of score.parts[index].measures) {
    for (const voice of measure.voices) collectEventIds(voice.items, removedEventIds);
  }

  const parts = score.parts.filter((_, partIndex) => partIndex !== index);
  if (!score.spanners || removedEventIds.size === 0) return { ...score, parts };

  const spanners = score.spanners.filter(
    (spanner) =>
      !removedEventIds.has(spanner.fromEventId) && !removedEventIds.has(spanner.toEventId),
  );
  return { ...score, parts, spanners };
}
